import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import useRealtimeResults from "../hooks/useRealtimeResults";
import RealtimeToastStack from "../components/RealtimeToastStack";

const RealtimeContext = createContext({
  events: [],
  jobs: new Map(),
  connectionState: "idle",
  lastEvent: null,
  registerResultOrigin: () => {},
});

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const realtime = useRealtimeResults({ token, enabled: Boolean(user) });
  const { lastEvent } = realtime;
  const [notifications, setNotifications] = useState([]);
  const resultOriginsRef = useRef(new Map());
  const splitDoneRef = useRef(new Map());

  const registerResultOrigin = ({ resultId, scannerId, scannerName } = {}) => {
    if (!resultId) return;
    const existing = resultOriginsRef.current.get(resultId) || {};
    resultOriginsRef.current.set(resultId, {
      ...existing,
      resultId,
      scannerId: scannerId || existing.scannerId,
      scannerName: scannerName || existing.scannerName,
      updatedAt: Date.now(),
    });

    // Keep the map bounded.
    if (resultOriginsRef.current.size > 200) {
      const entries = Array.from(resultOriginsRef.current.entries()).sort(
        (a, b) => (a[1]?.updatedAt || 0) - (b[1]?.updatedAt || 0)
      );
      const toDelete = entries.slice(0, entries.length - 200);
      toDelete.forEach(([key]) => resultOriginsRef.current.delete(key));
    }
  };

  useEffect(() => {
    if (!user) {
      setNotifications([]);
    }
  }, [user]);

  useEffect(() => {
    if (!lastEvent) return;
    if (!lastEvent.resultId) return;

    if (
      lastEvent.eventType === "completed" ||
      lastEvent.eventType === "error"
    ) {
      const origin = resultOriginsRef.current.get(lastEvent.resultId) || {};
      const scannerId =
        lastEvent.payload?.scannerId ||
        lastEvent.payload?.scanner_id ||
        lastEvent.scannerId ||
        origin.scannerId ||
        null;
      // Partes de un PDF con varios documentos: un solo aviso por PDF que va
      // contando, no uno por parte.
      const split = lastEvent.payload?.splitPart;
      let splitInfo = null;
      if (split?.parentFilename && lastEvent.eventType === "completed") {
        const key = `${split.parentFilename}|${split.total}`;
        const done = splitDoneRef.current.get(key) || new Set();
        done.add(split.index);
        splitDoneRef.current.set(key, done);
        splitInfo = { key, done: done.size, total: split.total };
      }
      const toast = {
        id: splitInfo ? `split-${splitInfo.key}` : lastEvent.eventId || `${lastEvent.resultId}-${Date.now()}`,
        groupKey: splitInfo ? `split-${splitInfo.key}` : lastEvent.resultId,
        split: splitInfo,
        resultId: lastEvent.resultId,
        eventType: lastEvent.eventType,
        filename: split?.parentFilename || lastEvent.payload?.filename,
        scannerId,
        scannerName: lastEvent.payload?.scannerName || origin.scannerName,
        timestamp: lastEvent.timestamp,
        status: lastEvent.payload?.status || lastEvent.eventType,
      };

      setNotifications((prev) => {
        const filtered = prev.filter(
          (item) => (item.groupKey || item.resultId) !== toast.groupKey
        );
        return [...filtered, toast].slice(-4);
      });
    }
  }, [lastEvent]);

  const dismissNotification = (id) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  };

  const value = useMemo(
    () => ({
      ...realtime,
      notifications,
      dismissNotification,
      registerResultOrigin,
    }),
    [realtime, notifications]
  );

  return (
    <RealtimeContext.Provider value={value}>
      {children}
      <RealtimeToastStack
        notifications={notifications}
        onDismiss={dismissNotification}
      />
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  return useContext(RealtimeContext);
}

export default RealtimeContext;
